/**
 * Fuel & Consumption Analysis ve panonun sefer kartları (aşama V6; README S21; ADR 0090). Ekran
 * adları İngilizce; Türkçe ikinci dil.
 */

export const fuelAnalysisEn = {
  title: 'Fuel & Consumption Analysis',
  description:
    'Consumption by fuel type and operating state, daily average, consumption per mile and the ROB flow reconciled with bunkering. Derived from the submitted reports.',
  vessel: 'Vessel',
  scope: 'Scope',
  scopes: { voyage: 'Voyage', period: 'Period' },
  voyage: 'Voyage',
  from: 'From (UTC)',
  to: 'To (UTC)',
  covers: '{{from}} → {{to}} · {{count}} reports',
  periodNote:
    'Consumption is counted from the second report in the period: the first report’s “since last report” figures belong to the time before it.',
  openNote:
    'The time after the last report has no consumption yet; daily averages use only the time closed by a report.',
  noVoyages: 'No voyage for this vessel yet.',
  noDataTitle: 'Nothing to analyse',
  noData: 'At least two submitted reports are needed in the selected scope.',
  error: 'The analysis could not be loaded.',
  figures: 'Key Figures',
  tiles: {
    total: 'Total Consumption',
    daily: 'Daily Average',
    seaDaily: 'Daily at Sea',
    perNm: 'At Sea per Mile',
    perNmSub: 'sea passage and waiting',
    distance: 'Distance',
    hours: 'Reported Time',
    warnings: 'ROB Differences',
    warningsNone: 'none',
    warningsSome: 'see ROB Flow',
  },
  states: {
    sea: 'At Sea',
    port: 'In Port',
    anchorage: 'At Anchor',
    drift: 'Drifting',
    waiting: 'Waiting',
  },
  byFuel: 'Consumption by Fuel (MT)',
  chart: 'Consumption by state',
  chartFuel: '{{fuel}}: {{total}} MT',
  passage: 'Strait and canal transit: {{value}} MT (part of at sea)',
  dailyTitle: 'Daily Average by State (MT/day)',
  columns: {
    fuel: 'Fuel',
    total: 'Total',
    daily: 'MT/day',
    perNm: 'kg/nm at sea',
    overall: 'Overall',
    opening: 'Opening ROB',
    bunkered: 'Bunkered',
    closing: 'Closing ROB',
    robConsumption: 'Consumed by ROB',
    reported: 'Reported',
    difference: 'Difference',
    status: 'Status',
    leg: 'Leg',
    from: 'From',
    to: 'To',
    start: 'Start (UTC)',
    end: 'End (UTC)',
    distance: 'Distance (nm)',
    duration: 'Duration',
    consumption: 'Consumption (MT)',
    kgPerNm: 'kg/nm',
    period: 'Period',
    place: 'Place',
    date: 'Date / Time (UTC)',
    report: 'Report',
    previous: 'Previous',
    consumed: 'Consumed',
    expected: 'Expected',
    rob: 'ROB',
  },
  reconciliation: 'Bunker Reconciliation',
  reconNote:
    'Opening ROB + bunkered − closing ROB is compared with the consumption written in the reports. Bunkering is entered in the Noon Report fuel table; a bunker received in port shows as a ROB increase. Differences up to {{tolerance}} MT are rounding.',
  status: {
    ok: 'Balanced',
    rob_higher: 'ROB higher',
    rob_lower: 'ROB lower',
    consumption_missing: 'Not reported',
    opening: 'Opening',
  },
  hints: {
    rob_higher: 'ROB is higher than expected: a bunker not recorded as such, or a correction.',
    rob_lower: 'ROB is lower than expected: consumption not written in the report, or a loss.',
    consumption_missing: 'ROB fell but no consumption is written in the report.',
  },
  legs: 'Consumption by Leg',
  noLegs: 'No sea passage in this scope.',
  periods: 'Consumption by Period',
  noPeriods: 'No period in this scope.',
  robFlow: 'ROB Flow',
  robFuel: 'Fuel',
  onlyIssues: 'Only differences',
  trend: 'ROB trend of {{fuel}} (MT)',
  trendPoint: '{{date}} · {{report}} · ROB {{rob}} MT',
  noFlow: 'No ROB recorded in this scope.',
  noIssues: 'No differences for this fuel.',
  issuesFound_one: '{{count}} difference',
  issuesFound_other: '{{count}} differences',
};

export const fuelAnalysisTr: typeof fuelAnalysisEn = {
  title: 'Yakıt ve Tüketim Analizi',
  description:
    'Yakıt türüne ve duruma göre tüketim, günlük ortalama, mil başına tüketim ve ikmalle mutabık ROB akışı. Gönderilmiş raporlardan türetilir.',
  vessel: 'Gemi',
  scope: 'Kapsam',
  scopes: { voyage: 'Sefer', period: 'Dönem' },
  voyage: 'Sefer',
  from: 'Başlangıç (UTC)',
  to: 'Bitiş (UTC)',
  covers: '{{from}} → {{to}} · {{count}} rapor',
  periodNote:
    'Tüketim dönemin ikinci raporundan sayılır: ilk raporun “son rapordan beri” değerleri ondan önceki zamana aittir.',
  openNote:
    'Son rapordan sonraki sürenin tüketimi henüz yazılmadı; günlük ortalamalar yalnız bir raporla kapanan süreyi kullanır.',
  noVoyages: 'Bu geminin henüz seferi yok.',
  noDataTitle: 'Analiz edilecek veri yok',
  noData: 'Seçilen kapsamda en az iki gönderilmiş rapor gerekir.',
  error: 'Analiz yüklenemedi.',
  figures: 'Temel Değerler',
  tiles: {
    total: 'Toplam Tüketim',
    daily: 'Günlük Ortalama',
    seaDaily: 'Denizde Günlük',
    perNm: 'Denizde Mil Başına',
    perNmSub: 'seyir ve bekleme',
    distance: 'Mesafe',
    hours: 'Raporlu Süre',
    warnings: 'ROB Farkları',
    warningsNone: 'yok',
    warningsSome: 'ROB Akışı’na bakın',
  },
  states: {
    sea: 'Denizde',
    port: 'Limanda',
    anchorage: 'Demirde',
    drift: 'Sürüklenmede',
    waiting: 'Beklemede',
  },
  byFuel: 'Yakıta Göre Tüketim (MT)',
  chart: 'Duruma göre tüketim',
  chartFuel: '{{fuel}}: {{total}} MT',
  passage: 'Boğaz ve kanal geçişi: {{value}} MT (denizdekinin parçası)',
  dailyTitle: 'Duruma Göre Günlük Ortalama (MT/gün)',
  columns: {
    fuel: 'Yakıt',
    total: 'Toplam',
    daily: 'MT/gün',
    perNm: 'Denizde kg/nm',
    overall: 'Genel',
    opening: 'Açılış ROB',
    bunkered: 'İkmal',
    closing: 'Kapanış ROB',
    robConsumption: 'ROB’a Göre Tüketim',
    reported: 'Raporlanan',
    difference: 'Fark',
    status: 'Durum',
    leg: 'Bacak',
    from: 'Nereden',
    to: 'Nereye',
    start: 'Başlangıç (UTC)',
    end: 'Bitiş (UTC)',
    distance: 'Mesafe (nm)',
    duration: 'Süre',
    consumption: 'Tüketim (MT)',
    kgPerNm: 'kg/nm',
    period: 'Dönem',
    place: 'Yer',
    date: 'Tarih / Saat (UTC)',
    report: 'Rapor',
    previous: 'Önceki',
    consumed: 'Tüketilen',
    expected: 'Beklenen',
    rob: 'ROB',
  },
  reconciliation: 'İkmal Mutabakatı',
  reconNote:
    'Açılış ROB + ikmal − kapanış ROB, raporlarda yazılan tüketimle karşılaştırılır. İkmal Öğle Raporu yakıt tablosuna girilir; limanda alınan ikmal ROB artışı olarak görünür. {{tolerance}} MT’ye kadar fark yuvarlamadır.',
  status: {
    ok: 'Tutarlı',
    rob_higher: 'ROB fazla',
    rob_lower: 'ROB eksik',
    consumption_missing: 'Yazılmamış',
    opening: 'Açılış',
  },
  hints: {
    rob_higher: 'ROB beklenenden fazla: ikmal olarak yazılmamış bir alım ya da düzeltme.',
    rob_lower: 'ROB beklenenden az: raporda yazılmamış tüketim ya da kayıp.',
    consumption_missing: 'ROB düşmüş ama raporda tüketim yazılmamış.',
  },
  legs: 'Bacağa Göre Tüketim',
  noLegs: 'Bu kapsamda seyir yok.',
  periods: 'Döneme Göre Tüketim',
  noPeriods: 'Bu kapsamda dönem yok.',
  robFlow: 'ROB Akışı',
  robFuel: 'Yakıt',
  onlyIssues: 'Yalnız farklar',
  trend: '{{fuel}} ROB eğilimi (MT)',
  trendPoint: '{{date}} · {{report}} · ROB {{rob}} MT',
  noFlow: 'Bu kapsamda ROB yazılmamış.',
  noIssues: 'Bu yakıtta fark yok.',
  issuesFound_one: '{{count}} fark',
  issuesFound_other: '{{count}} fark',
};

/** Panonun etkin sefer kartları (S19 eki; ADR 0090 §5). */
export const voyageCardsEn = {
  title: 'Active Voyages',
  none: 'No active voyage',
  noneNote: 'A voyage starts when the pilot disembarks on the Departure Report.',
  error: 'Voyages could not be loaded.',
  lastReport: 'Last report',
  noReport: 'No report yet',
  position: 'Position',
  next: 'Next operation',
  notSet: 'Not set',
  eta: 'ETA {{port}}',
  consumption: 'Consumption',
  consumptionValue: '{{total}} · {{daily}}/day',
  robOk: 'ROB balanced',
  robIssues_one: '{{count}} ROB difference',
  robIssues_other: '{{count}} ROB differences',
  details: 'Voyage Details',
  fuel: 'Fuel Analysis',
  progress: 'Voyage progress',
};

export const voyageCardsTr: typeof voyageCardsEn = {
  title: 'Süren Seferler',
  none: 'Süren sefer yok',
  noneNote: 'Sefer, Departure Report’ta pilotun ayrılmasıyla başlar.',
  error: 'Seferler yüklenemedi.',
  lastReport: 'Son rapor',
  noReport: 'Henüz rapor yok',
  position: 'Konum',
  next: 'Sonraki işlem',
  notSet: 'Seçilmedi',
  eta: 'ETA {{port}}',
  consumption: 'Tüketim',
  consumptionValue: '{{total}} · günde {{daily}}',
  robOk: 'ROB tutarlı',
  robIssues_one: '{{count}} ROB farkı',
  robIssues_other: '{{count}} ROB farkı',
  details: 'Sefer Ayrıntıları',
  fuel: 'Yakıt Analizi',
  progress: 'Sefer ilerlemesi',
};
